/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from "recharts";
import {
  MoreVertical,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  Wallet,
  Plus,
  X,
  Upload,
  BarChart2,
  RefreshCw,
  Filter,
} from "lucide-react";
import ReactApexChart from "react-apexcharts";
import { db } from "../../firebase";
import {
  addDoc,
  collection,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import Swal from "sweetalert2";
import axios from "axios";
import ImageCompressor from "react-image-file-resizer";
import { Spin } from "antd";
import { SERVER_URL } from "../../constants/server_url";
import { getAuth } from "firebase/auth";

// const API_URL = `${SERVER_URL.development}foorchat/chats`;
const API_URL = `${process.env.REACT_APP_BACKEND_DIGITALOCEAN_URL}/foorchat/stats`;

const FoorchatStats = () => {
  const [isChargeModalOpen, setIsChargeModalOpen] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("ccp");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isProcessingCharge, setIsProcessingCharge] = useState(false);

  const account = useSelector((state) => state.AccountReducer);
  const { t } = useTranslation();

  const [dateRange, setDateRange] = useState("today"); // today, yesterday, week, month
  const [statsData, setStatsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = async () => {
    if (!account?.id) {
      setLoading(false);
      setError(t("foorchat.stats.account_not_found"));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const user = getAuth().currentUser;
      if (!user) {
        throw new Error("Not authenticated");
      }
      const token = await user.getIdToken();

      const res = await fetch(`${API_URL}?storeId=${account.user}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "ngrok-skip-browser-warning": "true",
        },
      });

      if (!res.ok) throw new Error(t("foorchat.stats.fetch_error"));

      const json = await res.json();
      setStatsData(json.data);
    } catch (err) {
      console.error("error when get bot's data:", err);
      setError(t("foorchat.stats.generic_error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [account?.id]);

  const getDateRangeValues = () => {
    const now = new Date();
    let startDate = new Date(now);
    let endDate = new Date(now);

    switch (dateRange) {
      case "today":
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      case "yesterday":
        startDate.setDate(now.getDate() - 1);
        startDate.setHours(0, 0, 0, 0);
        endDate.setDate(now.getDate() - 1);
        endDate.setHours(23, 59, 59, 999);
        break;
      case "week":
        startDate.setDate(now.getDate() - 6); // the last 7 days including today
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      case "month":
        startDate.setDate(now.getDate() - 29); // the last 30 days including today
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      default:
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
    }

    return { startDate, endDate };
  };

  const analytics = useMemo(() => {
    if (!statsData) return null;

    const { startDate, endDate } = getDateRangeValues();

    // filter only the days that fall inside the selected range
    const filteredDaily = (statsData.daily || []).filter((d) => {
      const [day, month, year] = d.date.split("-").map(Number);
      const date = new Date(year, month - 1, day);
      return date >= startDate && date <= endDate;
    });

    const totals = filteredDaily.reduce(
      (acc, day) => {
        acc.totalChats += day.totalChats || 0;
        acc.botMessages += day.botMessages || 0;
        acc.totalMessages += day.totalMessages || 0;
        acc.totalAudios += day.totalAudios || 0;
        acc.totalImages += day.totalImages || 0;
        acc.orderCreated += day.orderCreated || 0;
        acc.spendSold += day.spendSold || 0;
        return acc;
      },
      {
        totalChats: 0,
        botMessages: 0,
        totalMessages: 0,
        totalAudios: 0,
        totalImages: 0,
        orderCreated: 0,
        spendSold: 0,
      },
    );

    const dailyStats = filteredDaily.map((d) => ({
      date: d.date,
      conversations: d.totalChats || 0,
      messages: d.totalMessages || 0,
      conversion: 0,
    }));

    // ads section
    const adMap = {};

    filteredDaily.forEach((day) => {
      (day.products || []).forEach((ad) => {
        if (!ad.productId) return;

        if (!adMap[ad.productId]) {
          adMap[ad.productId] = {
            adId: ad.productId,
            chats: 0,
            orders: 0,
            messages: 0,
          };
        }

        adMap[ad.productId].chats += ad.totalChats || 0;
        adMap[ad.productId].orders += ad.orderCreated || 0;
        adMap[ad.productId].messages += ad.totalMessages || 0;
      });
    });

    const adStats = Object.values(adMap)
      .map((ad) => ({
        ...ad,
        conversionRate: ad.chats > 0 ? (ad.orders / ad.chats) * 100 : 0,
      }))
      .sort((a, b) => b.chats - a.chats);

    const result = {
      totalChats: totals.totalChats,
      totalBotMessages: totals.botMessages,
      totalMessages: totals.totalMessages,
      voiceMessages: totals.totalAudios,
      imageMessages: totals.totalImages,
      createdOrders: totals.orderCreated,
      totalCosts: totals.spendSold,
      currentBalance: statsData.balance ?? 0,
      conversionRate: 0,

      dailyStats,
      messageTypeChartData: [
        {
          name: t("foorchat.stats.text_messages"),
          value: totals.totalMessages,
          color: "#9b59bf",
        },
        {
          name: t("foorchat.stats.voice_messages"),
          value: totals.totalAudios,
          color: "#e1a626",
        },
        {
          name: t("foorchat.stats.images_files"),
          value: totals.totalImages,
          color: "#25c7bf",
        },
        {
          name: t("foorchat.stats.other"),
          value: 0,
          color: "#49a688",
        },
      ],
      adStats,
    };

    return result;
  }, [statsData, dateRange]);

  const uploadImage = async (file) => {
    return new Promise(async (resolve, reject) => {
      try {
        const dataImage = new FormData();

        if (file.type === "application/pdf") {
          dataImage.append("upload", file, new Date().getTime() + "proof");
        } else {
          const uri = await new Promise((resolveResize) => {
            ImageCompressor.imageFileResizer(
              file,
              Infinity,
              Infinity,
              "WEBP",
              100,
              0,
              (uri) => {
                resolveResize(uri);
              },
              "base64",
            );
          });

          const blob = await fetch(uri).then((r) => r.blob());
          dataImage.append("upload", blob, new Date().getTime() + "proof");
        }

        const response = await axios.post(
          `https://octopus-app-i4ylg.ondigitalocean.app/upload`,
          dataImage,
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          },
        );

        resolve(response.data.url);
      } catch (error) {
        console.error("Error uploading image:", error);
        reject(error);
      }
    });
  };

  const handleChargeBalance = async () => {
    if (!selectedAmount || !uploadedFile || !account?.id) {
      Swal.fire({
        icon: "error",
        title: t("foorchat.stats.error"),
        text: t("foorchat.stats.please_select_amount_and_proof"),
      });
      return;
    }

    setIsProcessingCharge(true);

    try {
      const proofUrl = await uploadImage(uploadedFile);

      const chargeTransaction = {
        plan: 10,
        planName: t("foorchat.stats.charge_plan_name"),
        paymentMethod:
          selectedPaymentMethod === "ccp"
            ? 1
            : selectedPaymentMethod === "baridimob"
              ? 2
              : selectedPaymentMethod === "redotpay"
                ? 5
                : 7,
        total: selectedAmount,
        total1: selectedAmount,
        proof: proofUrl,
        timeStamp: serverTimestamp(),
        isValid: 2,
        user: account.user,
        email: account.email || "",
        name: account.name || "",
        phone: account.phone || "",
        chargeAmount: selectedAmount,
        timestamp: serverTimestamp(),
      };

      await addDoc(collection(db, "transaction"), chargeTransaction);

      const accountRef = doc(db, "account", account.id);

      const updateData = {
        "foorchatSolde.lastUsed": serverTimestamp(),
      };

      await updateDoc(accountRef, updateData);

      Swal.fire({
        icon: "success",
        title: t("foorchat.stats.charge_success_title"),
        text: t("foorchat.stats.charge_success_text", {
          amount: selectedAmount,
        }),
        confirmButtonColor: "#9b59bf",
      });

      setIsChargeModalOpen(false);
      setSelectedAmount(null);
      setUploadedFile(null);
      setSelectedPaymentMethod("ccp");
    } catch (error) {
      console.error("Error processing charge:", error);
      Swal.fire({
        icon: "error",
        title: t("foorchat.stats.charge_error_title"),
        text: t("foorchat.stats.charge_error_text"),
      });
    } finally {
      setIsProcessingCharge(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="m-3 flex items-center justify-center min-h-[60vh]">
        <div className="w-full max-w-md text-center px-6 py-10 bg-white dark:!bg-[#181819] rounded-2xl shadow-sm border border-[#fbf4ff] dark:!border-[#3c3a3d]">
          <div className="mx-auto mb-5 w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
            <BarChart2 size={28} className="text-red-500 dark:text-red-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">
            {t("foorchat.stats.error_title")}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 leading-relaxed">
            {error}
          </p>
          <button
            onClick={fetchStats}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#9e68ba] text-white text-sm font-medium hover:bg-[#8a5aa5] active:scale-[0.98] transition-all shadow-sm"
          >
            <RefreshCw size={16} />
            {t("foorchat.stats.retry")}
          </button>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="m-3 flex flex-col items-center justify-center min-h-[60vh] px-4 text-center">
        <div className="w-16 h-16 rounded-full bg-purple-50 dark:bg-[#6e358c]/20 flex items-center justify-center mb-4">
          <BarChart2 size={28} className="text-[#9e68ba]" />
        </div>
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-1">
          {t("foorchat.stats.empty_title")}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
          {t("foorchat.stats.empty_message")}
        </p>
      </div>
    );
  }

  const stats = [
    {
      name: t("foorchat.stats.currentCost"),
      price: `$${(analytics.totalCosts || 0).toFixed(20)}`,
      change: "+0%",
      isUp: true,
      logo: "https://cdn-icons-png.flaticon.com/128/10308/10308618.png",
      subtitle: `${analytics.totalBotMessages || 0} ${t("foorchat.stats.botMessages")}`,
    },
    {
      name: t("foorchat.stats.totalChats"),
      price: (analytics.totalChats || 0).toLocaleString(),
      change: "+12.3%",
      isUp: true,
      logo: "https://cdn-icons-png.flaticon.com/128/3616/3616040.png",
    },
    {
      name: t("foorchat.stats.messages_today"),
      price: (analytics.totalMessages || 0).toLocaleString(),
      change: "+8.5%",
      isUp: true,
      logo: "https://cdn-icons-png.flaticon.com/128/11774/11774073.png",
    },
    {
      name: t("foorchat.stats.new_orders"),
      price: (analytics.createdOrders || 0).toLocaleString(),
      change: "+22.1%",
      isUp: true,
      logo: "https://cdn-icons-png.flaticon.com/128/6815/6815043.png",
    },
    {
      name: t("foorchat.stats.avg_cost_per_chat"),
      price:
        analytics.totalChats > 0
          ? `$${(analytics.totalCosts / analytics.totalChats).toFixed(20)}`
          : "$0.0000",
      change: "+0%",
      isUp: true,
      logo: "https://cdn-icons-png.flaticon.com/128/2593/2593514.png",
    },
    {
      name: t("foorchat.stats.conversion_rate"),
      price:
        analytics.totalChats > 0
          ? `${((analytics.createdOrders / analytics.totalChats) * 100).toFixed(1)}%`
          : "0%",
      change: "+5.2%",
      isUp: true,
      logo: "https://cdn-icons-png.flaticon.com/128/951/951764.png",
    },
    {
      name: t("foorchat.stats.available_balance"),
      price: `$${(analytics.currentBalance || 0).toFixed(2)}`,
      change: "+11.01%",
      isUp: true,
      logo: "https://cdn-icons-png.flaticon.com/128/9165/9165717.png",
      hasButton: true,
    },
  ];

  // إعدادات مخطط المحادثات اليومية
  const conversationChartOptions = {
    chart: {
      type: "bar",
      height: 200,
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        columnWidth: "60%",
      },
    },
    dataLabels: { enabled: false },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
    grid: {
      strokeDashArray: 0,
      borderColor: "#e0e0e0",
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    xaxis: {
      categories: analytics.dailyStats.map((stat) => stat.date),
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        style: {
          fontSize: "12px",
          fontFamily: "Outfit, sans-serif",
          colors: "#373d3f",
        },
      },
    },
    yaxis: {
      tickAmount: 4,
      min: 0,
      labels: {
        style: {
          fontSize: "11px",
          fontFamily: "Outfit, sans-serif",
          colors: "#373d3f",
        },
      },
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.25,
        gradientToColors: undefined,
        inverseColors: false,
        opacityFrom: 0.9,
        opacityTo: 0.5,
        stops: [0, 100],
      },
    },
    colors: ["rgba(155, 89, 191, 1)"],
    tooltip: {
      y: {
        formatter: (val) => `${val} ${t("foorchat.stats.conversation")}`,
      },
    },
  };

  const conversationSeries = [
    {
      name: t("foorchat.stats.conversations"),
      data: analytics.dailyStats.map((stat) => stat.conversations),
    },
  ];

  const chargeAmounts = [
    { value: 10, label: "$10", popular: false },
    { value: 25, label: "$25", popular: true },
    { value: 50, label: "$50", popular: false },
    { value: 100, label: "$100", popular: false },
  ];

  const paymentMethods = [
    {
      id: "ccp",
      name: t("foorchat.stats.ccp"),
      description: t("foorchat.stats.ccpDescription"),
      icon: <img src="/logo-round.png" alt={t("foorchat.stats.ccp")} className="w-6 h-6" />,
      info: {
        [t("foorchat.stats.accountNumber")]: "0043101520 clé 41",
        [t("foorchat.stats.accountName")]: "BENKHEDDA AHMED",
      },
    },
    {
      id: "baridimob",
      name: t("foorchat.stats.baridimob"),
      description: t("foorchat.stats.paymentScreenshotDescription"),
      icon: <img src="/baridimob.png" alt={t("foorchat.stats.baridimob")} className="w-6 h-6" />,
      info: {
        [t("foorchat.stats.accountNumber")]: "00799999004310152041",
      },
    },
    {
      id: "redotpay",
      name: t("foorchat.stats.redotpay"),
      description: t("foorchat.stats.paymentScreenshotDescription"),
      icon: (
        <img
          src="https://www.redotpay.com/img/favicon.png"
          alt={t("foorchat.stats.redotpay")}
          className="w-6 h-6"
        />
      ),
      info: {
        [t("foorchat.stats.accountId")]: "1546009587",
      },
    },
    {
      id: "bank",
      name: t("foorchat.stats.europeanBankTransfer"),
      description: t("foorchat.stats.paymentScreenshotDescription"),
      icon: (
        <img
          src="https://cdn-icons-png.flaticon.com/128/1770/1770992.png"
          alt={t("ui.bank")}
          className="w-6 h-6"
        />
      ),
      info: {
        IBAN: "CH4308843125935321010",
        BIC: "DUBACHGG",
        [t("foorchat.stats.accountHolder")]: "Salim Benaouda",
        [t("foorchat.stats.beneficiaryAddress")]:
          "cite mokhbat, 1, 19, 09000, Blida",
        [t("foorchat.stats.bankAddress")]:
          "Route de Pré-Bois 20, 1215 Geneva, Switzerland",
      },
    },
  ];

  const getSelectedPaymentMethod = () =>
    paymentMethods.find((method) => method.id === selectedPaymentMethod);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) setUploadedFile(file);
  };

  return (
    <div className="p-4 mx-auto max-w-[1440px] md:p-6 bg-gray-50 dark:bg-gray-950 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 mb-1">
            {t("foorchat.stats.paymentAnalytics")}
          </h1>
          <p className="text-sm text-gray-500">
            {t("foorchat.stats.paymentAnalyticsDescription")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md ${
              analytics.currentBalance > 10
                ? "text-green-700 bg-green-100"
                : "text-red-700 bg-red-100"
            }`}
          >
            {analytics.currentBalance > 10
              ? t("foorchat.stats.balanceSufficient")
              : t("foorchat.stats.lowBalance")}
          </span>
          <button
            onClick={() => setIsChargeModalOpen(true)}
            className="flex items-center justify-center gap-1 px-3 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition-colors"
          >
            <Plus className="w-4 h-4" /> {t("foorchat.stats.chargeBalance")}
          </button>
        </div>
      </div>

      {/* Date Filter */}
      <div className="bg-white dark:!bg-[#181919] rounded-xl shadow-sm p-4 mb-6 border border-purple-100 dark:!border-[#3c3a3d]">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={18} className="text-[#9e68ba]" />
          <h2 className="text-base font-semibold text-[#9e68ba]">
            {t("foorchat.stats.filterByDate")}
          </h2>
        </div>

        <div className="bg-white dark:!bg-[#181919] rounded-xl shadow-sm border border-purple-100 dark:!border-[#3c3a3d] overflow-hidden inline-flex">
          {[
            { value: "today", label: t("foorchat.stats.today") },
            { value: "yesterday", label: t("foorchat.stats.yesterday") },
            { value: "week", label: t("foorchat.stats.thisWeek") },
            { value: "month", label: t("foorchat.stats.thisMonth") },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => setDateRange(option.value)}
              className={`px-5 py-2.5 text-sm font-medium transition-colors ${
                dateRange === option.value
                  ? "bg-purple-50 dark:!bg-[#522868] text-[#9e68ba] dark:!text-gray-200 border-b-2 border-[#9b59bf]"
                  : "text-gray-600 dark:!text-gray-400 hover:bg-purple-50 dark:hover:bg-[#2a2a2c]"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4 md:gap-6">
        {/* Foorchat Analytics Cards */}
        <div className="col-span-12">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6 xl:grid-cols-4">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="rounded-2xl border border-gray-200 bg-white px-6 pb-5 pt-6 dark:border-gray-800 dark:bg-white/[0.03]"
              >
                <div className="flex items-center gap-3 mb-6">
                  <img src={stat.logo} alt={t("ui.logo")} className="w-6 h-6" />
                  <div>
                    <h3 className="text-base font-semibold text-gray-800 dark:text-white/90">
                      {stat.name}
                    </h3>
                    {stat.subtitle && (
                      <p className="text-xs text-gray-500 mt-1">
                        {stat.subtitle}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-end justify-between">
                  <h4 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                    {stat.price}
                  </h4>

                  <div className="flex items-center gap-2">
                    {stat.hasButton && (
                      <button
                        onClick={() => setIsChargeModalOpen(true)}
                        className="flex items-center gap-1 px-2 py-1 bg-purple-600 text-white rounded text-xs hover:bg-purple-700"
                      >
                        <Plus className="w-3 h-3" />{" "}
                        {t("foorchat.stats.charge")}
                      </button>
                    )}
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${
                        stat.isUp
                          ? "bg-green-50 text-green-600"
                          : "bg-red-50 text-red-600"
                      }`}
                      dir="rtl"
                    >
                      {stat.isUp ? (
                        <TrendingUp className="w-3 h-3" />
                      ) : (
                        <TrendingDown className="w-3 h-3" />
                      )}
                      {stat.change}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Analytics Charts & Performance */}
        <div className="col-span-12 space-y-6 xl:col-span-8">
          {/* Daily Conversations Chart */}
          <div className="rounded-2xl border border-gray-200 bg-white px-5 pb-5 pt-5 dark:border-gray-800 dark:bg-white/[0.03] sm:px-6 sm:pt-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                {t("foorchat.stats.conversations_messages_analysis")}
              </h3>
              <div className="relative inline-block">
                <button className="dropdown-toggle">
                  <MoreVertical className="w-6 h-6 text-gray-400 hover:text-gray-700 dark:hover:text-gray-300" />
                </button>
              </div>
            </div>
            <div className="h-[350px] -ml-4">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={analytics.dailyStats}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="conversations"
                    stackId="1"
                    stroke="#9b59bf"
                    fill="#9b59bf"
                    fillOpacity={0.6}
                    name={t("foorchat.stats.conversations")}
                  />
                  <Area
                    type="monotone"
                    dataKey="messages"
                    stackId="2"
                    stroke="#e1a626"
                    fill="#e1a626"
                    fillOpacity={0.6}
                    name={t("foorchat.stats.messages")}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ads section */}
          <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] sm:p-6">
            <div className="flex flex-col gap-2 px-5 mb-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                  {t("foorchat.stats.ads_performance")}
                </h3>
              </div>
            </div>

            <div className="overflow-hidden">
              <div className="max-w-full px-5 overflow-x-auto sm:px-6">
                <table className="min-w-full">
                  <thead className="border-y border-gray-200 dark:border-gray-800">
                    <tr>
                      <th className="py-3 font-normal text-start text-sm text-gray-500 dark:text-gray-400">
                        {t("foorchat.stats.adId")}
                      </th>
                      <th className="px-4 py-3 font-normal text-center text-sm text-gray-500 dark:text-gray-400">
                        {t("foorchat.stats.conversations")}
                      </th>
                      <th className="px-4 py-3 font-normal text-center text-sm text-gray-500 dark:text-gray-400">
                        {t("foorchat.stats.orders")}
                      </th>
                      <th className="px-4 py-3 font-normal text-center text-sm text-gray-500 dark:text-gray-400">
                        {t("foorchat.stats.conversionRate")}
                      </th>
                      <th className="px-4 py-3 font-normal text-center text-sm text-gray-500 dark:text-gray-400">
                        {t("foorchat.stats.totalMessages")}
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {analytics.adStats.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="py-10 text-center text-sm text-gray-400"
                        >
                          {t("foorchat.stats.noAdsData")}
                        </td>
                      </tr>
                    ) : (
                      analytics.adStats.slice(0, 8).map((ad) => (
                        <tr key={ad.adId}>
                          <td className="py-4 px-4">
                            <div className="font-medium text-gray-900 dark:text-white font-mono text-sm">
                              {ad.adId}
                            </div>
                          </td>

                          <td className="py-4 px-4 text-center">
                            <span className="bg-purple-50 text-purple-600 px-2.5 py-1 rounded-md text-xs font-medium">
                              {ad.chats}
                            </span>
                          </td>

                          <td className="py-4 px-4 text-center">
                            <span className="bg-green-50 text-green-600 px-2.5 py-1 rounded-md text-xs font-medium">
                              {ad.orders}
                            </span>
                          </td>

                          <td className="py-4 px-4 text-center">
                            <span
                              className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                                ad.conversionRate > 20
                                  ? "bg-green-100 text-green-800"
                                  : ad.conversionRate > 10
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-red-100 text-red-800"
                              }`}
                            >
                              {ad.conversionRate.toFixed(1)}%
                            </span>
                          </td>

                          <td className="py-4 px-4 text-center font-medium text-gray-900 dark:text-white">
                            {ad.messages.toLocaleString()}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Message Analytics & User Stats */}
        <div className="col-span-12 space-y-6 xl:col-span-4">
          {/* Daily Conversations Chart */}
          <div className="rounded-2xl border border-gray-200 bg-white px-5 pt-5 dark:border-gray-800 dark:bg-white/[0.03] sm:px-6 sm:pt-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                {t("foorchat.stats.dailyConversations")}
              </h3>
              <div className="relative inline-block">
                <button className="dropdown-toggle">
                  <MoreVertical className="w-6 h-6 text-gray-400 hover:text-gray-700 dark:hover:text-gray-300" />
                </button>
              </div>
            </div>
            <div className="-ml-5">
              <div style={{ minHeight: 215 }} className="w-full">
                <ReactApexChart
                  options={conversationChartOptions}
                  series={conversationSeries}
                  type="bar"
                  height={200}
                />
              </div>
            </div>
          </div>

          {/* Message Types Distribution */}
          <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                {t("foorchat.stats.messageTypesDistribution")}
              </h3>
              <button className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                <MoreVertical className="w-6 h-6" />
              </button>
            </div>
            <div className="flex h-[300px] flex-col items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={analytics.messageTypeChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    labelLine={false}
                    label={false}
                  >
                    {analytics.messageTypeChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              {analytics.messageTypeChartData.map((item, index) => (
                <div key={index} className="flex items-center">
                  <div
                    className="w-3 h-3 rounded-full mr-2"
                    style={{ backgroundColor: item.color }}
                  ></div>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {item.name}: {item.value.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ===== Charge Balance Modal ===== */}
      {isChargeModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {t("foorchat.stats.chargeBalance")}
              </h2>
              <button
                onClick={() => setIsChargeModalOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
                disabled={isProcessingCharge}
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Current Balance */}
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">
                      {t("foorchat.stats.currentBalance")}
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      ${analytics.currentBalance?.toFixed(2) || "0.00"}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <Wallet className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </div>

              {/* Amount Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-3">
                  {t("foorchat.stats.selectChargeAmount")}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {chargeAmounts.map(({ value, label, popular }) => (
                    <button
                      key={value}
                      onClick={() => setSelectedAmount(value)}
                      disabled={isProcessingCharge}
                      className={`relative p-4 border-2 rounded-lg transition-all ${
                        selectedAmount === value
                          ? "border-purple-500 bg-purple-50"
                          : "border-gray-200 hover:border-gray-300"
                      } ${
                        isProcessingCharge
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      {popular && (
                        <span className="absolute -top-2 right-2 bg-purple-600 text-white text-xs px-2 py-0.5 rounded-full">
                          {t("foorchat.stats.mostPopular")}
                        </span>
                      )}
                      <div className="text-lg font-semibold text-gray-900">
                        {label}
                      </div>
                    </button>
                  ))}
                </div>
                <div className="mt-4">
                  <label className="block text-sm text-gray-600 mb-2">
                    {t("foorchat.stats.customAmount")}
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      placeholder="0.00"
                      min="1"
                      disabled={isProcessingCharge}
                      onChange={(e) =>
                        setSelectedAmount(parseFloat(e.target.value) || null)
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                    />
                    <span className="absolute left-3 top-3 text-gray-500">
                      $
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-3">
                  {t("foorchat.stats.paymentMethod")}
                </label>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {paymentMethods.map((method) => (
                    <button
                      key={method.id}
                      onClick={() => setSelectedPaymentMethod(method.id)}
                      disabled={isProcessingCharge}
                      className={`flex flex-row items-center gap-2 p-3 border-2 rounded-lg transition-all ${
                        selectedPaymentMethod === method.id
                          ? "border-purple-500 bg-purple-50"
                          : "border-gray-200 hover:border-gray-300"
                      } ${
                        isProcessingCharge
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      <span className="text-2xl">{method.icon}</span>
                      <span className="text-xs font-medium text-gray-900 leading-tight">
                        {method.name}
                      </span>
                    </button>
                  ))}
                </div>
                {getSelectedPaymentMethod() && (
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                      {getSelectedPaymentMethod().icon}{" "}
                      {t("foorchat.stats.paymentInformation")} -{" "}
                      {getSelectedPaymentMethod().name}
                    </h4>
                    <div className="space-y-3">
                      {Object.entries(getSelectedPaymentMethod().info).map(
                        ([key, value]) => (
                          <div
                            key={key}
                            className="bg-white p-3 rounded-lg border"
                          >
                            <div className="text-xs text-gray-600 mb-1 font-medium">
                              {key}:
                            </div>
                            <div className="text-sm text-gray-900 break-all font-mono">
                              {value}
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                    <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                      💡 {getSelectedPaymentMethod().description}
                    </div>
                  </div>
                )}
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-3">
                  {t("foorchat.stats.paymentProof")}
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-purple-300 transition-colors">
                  <input
                    type="file"
                    id="payment-proof"
                    accept="image/*,.pdf"
                    onChange={handleFileUpload}
                    disabled={isProcessingCharge}
                    className="hidden"
                  />
                  <label
                    htmlFor="payment-proof"
                    className={`cursor-pointer ${
                      isProcessingCharge ? "cursor-not-allowed" : ""
                    }`}
                  >
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600 mb-1">
                      {t("foorchat.stats.clickToUploadPaymentProof")}
                    </p>
                    <p className="text-xs text-gray-500">
                      {t("foorchat.stats.paymentProofFormats")}
                    </p>
                  </label>
                  {uploadedFile && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <p className="text-sm text-green-700 font-medium">
                        {t("foorchat.stats.uploadedFile")}: {uploadedFile.name}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* New Balance Preview */}
              {selectedAmount && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-green-700">
                      {t("foorchat.stats.newBalanceAfterCharge")}
                    </span>
                    <span className="text-lg font-semibold text-green-800">
                      ${(analytics.currentBalance + selectedAmount).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setIsChargeModalOpen(false)}
                disabled={isProcessingCharge}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                {t("foorchat.stats.cancel")}
              </button>
              <button
                onClick={handleChargeBalance}
                disabled={
                  !selectedAmount ||
                  !uploadedFile ||
                  !selectedPaymentMethod ||
                  isProcessingCharge
                }
                className={`flex-1 px-4 py-3 rounded-lg transition-colors flex items-center justify-center gap-2 ${
                  selectedAmount &&
                  uploadedFile &&
                  selectedPaymentMethod &&
                  !isProcessingCharge
                    ? "bg-purple-600 text-white hover:bg-purple-700"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                }`}
              >
                {isProcessingCharge ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {t("foorchat.stats.processing")}
                  </>
                ) : (
                  t("foorchat.stats.submitChargeRequest")
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FoorchatStats;
